import { useCallback, useEffect, useLayoutEffect } from 'react';

import { useLocation } from '@remix-run/react';

const STORAGE_KEY = 'positions';

function getPositions() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    // If parsing fails, return an empty object
    return {};
  }
}

export function ScrollRestoration() {
  const location = useLocation();

  // Store the scroll position in sessionStorage on scrollend
  const handler = useCallback(() => {
    const positions = getPositions();
    const key = window.history.state?.key || Math.random().toString(32).slice(2);
    positions[key] = window.scrollY;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  }, []);

  useEffect(() => {
    return monitorUrlChanges(handler);
  }, [handler]);

  // restore the scroll position if history matches
  // otherwise scroll to the top or to the hash
  useLayoutEffect(() => {
    if (!window.history.state || !window.history.state.key) {
      const key = Math.random().toString(32).slice(2);
      window.history.replaceState({ key }, '');
    }
    try {
      const key = window.history.state.key;
      const positions = getPositions();
      const storedY = positions[key];
      if (typeof storedY === 'number') {
        setTimeout(() => {
          window.scrollTo({ left: 0, top: storedY, behavior: 'instant' });
        }, 500);
        return;
      }
    } catch {
      // If there's an error with the stored position, we just ignore it
      // and remove the key from sessionStorage
      sessionStorage.removeItem(STORAGE_KEY);
    }

    // try to scroll to the hash
    try {
      if (location.hash) {
        const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
        if (el) {
          el.scrollIntoView();
          return;
        }
      }
    } catch {
      // If there's an error with the hash, we just ignore it
      // and scroll to the top
    }

    // otherwise go to the top on new locations
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

function monitorUrlChanges(onUrlChange: () => void) {
  // Save references to the original methods
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  // Override history.pushState
  history.pushState = function (...args) {
    onUrlChange();
    originalPushState.apply(this, args);
  };

  // Override history.replaceState
  history.replaceState = function (...args) {
    onUrlChange();
    originalReplaceState.apply(this, args);
  };

  return () => {
    // Restore original methods
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
  };
}
